import { useEffect, useState } from 'react'
import { EventData, Joyride, Step } from 'react-joyride'
import { OnboardingAnchor } from './tour.anchor'
import { OnboardingTooltip } from './tour.tooltip'
import { OnboardingStep, OnboardingStepData } from './tour.types'
import './onboarding.css'

interface OnboardingTourUIProps {
  run: boolean
  stepIndex: number
  steps: OnboardingStep[]
  onCallback: (data: EventData) => void
  onNext: () => void
  onBack: () => void
  onSkip: () => void
  labels: {
    back: string
    next: string
    nextWithProgress: string
    skip: string
    finish: string
    tryThis: string
  }
}

const stepSelector = (step?: Step) =>
  typeof step?.target === 'string' ? step.target : null

export const OnboardingTourUI = ({
  run,
  stepIndex,
  steps,
  onCallback,
  onNext,
  onBack,
  onSkip,
  labels,
}: OnboardingTourUIProps) => {
  const [disableAutoScroll, setDisableAutoScroll] = useState(() =>
    window.matchMedia('(max-width: 1400px), (max-height: 760px)').matches
  )

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1400px), (max-height: 760px)')
    const handleMediaChange = (event: MediaQueryListEvent) => {
      setDisableAutoScroll(event.matches)
    }

    setDisableAutoScroll(mediaQuery.matches)
    mediaQuery.addEventListener('change', handleMediaChange)

    return () => mediaQuery.removeEventListener('change', handleMediaChange)
  }, [])

  const step = steps[stepIndex]
  const target = stepSelector(step)

  return (
    <>
      <Joyride
        onEvent={onCallback}
        run={run}
        stepIndex={stepIndex}
        steps={steps}
        floatingOptions={{
          hideArrow: true,
          strategy: 'fixed',
        }}
        styles={{
          floater: {
            display: 'none',
            opacity: 0,
            pointerEvents: 'none',
          },
        }}
        options={{
          arrowColor: '#ffffff',
          backgroundColor: '#ffffff',
          buttons: ['back', 'primary', 'skip'],
          dismissKeyAction: false,
          overlayClickAction: false,
          overlayColor: 'rgba(5, 14, 46, 0.52)',
          primaryColor: '#00146D',
          showProgress: false,
          blockTargetInteraction: true,
          offset: 16,
          scrollDuration: disableAutoScroll ? 0 : 450,
          scrollOffset: disableAutoScroll ? 0 : 20,
          skipScroll: disableAutoScroll,
          spotlightPadding: 10,
          spotlightRadius: 12,
          targetWaitTimeout: 2500,
          textColor: '#0B1E2D',
          width: 360,
          zIndex: 1800,
        }}
        continuous
        scrollToFirstStep={!disableAutoScroll}
        locale={{
          back: labels.back,
          close: labels.finish,
          last: labels.finish,
          next: labels.next,
          nextWithProgress: labels.nextWithProgress,
          skip: labels.skip,
        }}
      />
      <OnboardingAnchor
        active={run && Boolean(target)}
        target={target}
        placement={typeof step?.placement === 'string' ? step.placement : 'right'}
      >
        <OnboardingTooltip
          index={stepIndex}
          size={steps.length}
          title={step?.title}
          content={step?.content}
          data={step?.data as OnboardingStepData | undefined}
          labels={labels}
          onBack={onBack}
          onNext={onNext}
          onSkip={onSkip}
        />
      </OnboardingAnchor>
    </>
  )
}
